import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { sql, PostgreSQL } from '@codemirror/lang-sql';
import { autocompletion } from '@codemirror/autocomplete';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';
import { searchKeymap } from '@codemirror/search';
function isDarkMode() {
    return document.documentElement.getAttribute('data-bs-theme') === 'dark';
}
/** Build CodeMirror table completions from table name list */
function buildCompletionSchema(tables) {
    const schema = {};
    const tableCompletions = [];
    for (const t of tables) {
        schema[t] = []; // columns not pre-loaded; user can use SchemaBrowser sidebar
        tableCompletions.push({
            label: t,
            apply: `"${t}"`,
            detail: 'table',
            type: 'class',
            boost: 10,
        });
    }
    return { schema, tables: tableCompletions };
}
const SqlEditor = forwardRef(({ value, onChange, tables, height = '160px' }, ref) => {
    const containerRef = useRef(null);
    const viewRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    useImperativeHandle(ref, () => ({
        insertText(text) {
            const view = viewRef.current;
            if (!view)
                return;
            const { from } = view.state.selection.main;
            view.dispatch({ changes: { from, insert: text } });
            view.focus();
        }
    }));
    useEffect(() => {
        if (!containerRef.current)
            return;
        const { schema, tables: tableCompletions } = buildCompletionSchema(tables);
        const dark = isDarkMode();
        const extensions = [
            keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
            history(),
            sql({ dialect: PostgreSQL, schema, tables: tableCompletions, upperCaseKeywords: true }),
            autocompletion(),
            syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
            EditorView.updateListener.of(update => {
                if (update.docChanged) {
                    onChangeRef.current(update.state.doc.toString());
                }
            }),
        ];
        if (dark) {
            extensions.push(oneDark);
            extensions.push(EditorView.theme({
                '&': { height, borderRadius: '0.375rem', border: '1px solid var(--bs-border-color, #495057)' },
                '.cm-scroller': { overflow: 'auto', fontFamily: 'var(--bs-font-monospace)', fontSize: '0.875rem' },
                '.cm-focused': { outline: '2px solid #86b7fe' },
                '.cm-content': { padding: '8px' },
                '.cm-tooltip': { backgroundColor: '#1e1e2e', border: '1px solid #495057', color: '#dee2e6' },
                '.cm-tooltip-autocomplete ul li': { color: '#dee2e6' },
                '.cm-tooltip-autocomplete ul li[aria-selected]': { backgroundColor: '#3b82f6', color: '#fff' },
                '.cm-completionLabel': { color: '#dee2e6' },
                '.cm-completionDetail': { color: '#9ca3af', fontStyle: 'italic' },
            }));
        }
        else {
            extensions.push(EditorView.theme({
                '&': { height, border: '1px solid var(--bs-border-color, #dee2e6)', borderRadius: '0.375rem' },
                '.cm-scroller': { overflow: 'auto', fontFamily: 'var(--bs-font-monospace)', fontSize: '0.875rem' },
                '.cm-focused': { outline: '2px solid #86b7fe' },
                '.cm-content': { padding: '8px' },
            }));
        }
        const state = EditorState.create({ doc: value, extensions });
        const view = new EditorView({ state, parent: containerRef.current });
        viewRef.current = view;
        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, [tables]); // eslint-disable-line react-hooks/exhaustive-deps
    // Sync external value changes
    useEffect(() => {
        const view = viewRef.current;
        if (!view)
            return;
        const currentDoc = view.state.doc.toString();
        if (value !== currentDoc) {
            view.dispatch({
                changes: { from: 0, to: currentDoc.length, insert: value }
            });
        }
    }, [value]);
    return _jsx("div", { ref: containerRef });
});
export default SqlEditor;
